import { CheckIcon } from '@heroicons/react/16/solid';
import classNames from 'classnames';
import { Controller, FieldPath, FieldValues, useFormContext } from 'react-hook-form';
import { FormFeedback } from './FormFeedback';
import { FormField } from './FormField';
import { FormLabel } from './FormLabel';
import { getFieldError } from './formUtils';
import { FormFieldProps } from './interfaces';

interface Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends FormFieldProps<TFieldValues, TName> {
  style?: string;
  rowReverse?: boolean;
  /**
   * Distinguishes two controls bound to the same form value, such as the
   * inline and dialog versions of the terms checkbox.
   */
  controlId?: string;
}
export const Checkbox = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: Props<TFieldValues, TName>
) => {
  const {
    name,
    label,
    description,
    cyTag,
    required,
    tooltip,
    className,
    errorCyTag,
    reserveFeedbackSpace,
    disabled,
    controlId = name,
  } = props;
  const {
    formState: { errors },
  } = useFormContext();
  const { rowReverse } = props;
  const error = getFieldError(errors, name);
  return (
    <Controller
      name={name}
      render={({ field }) => {
        return (
          <FormField
            id={controlId}
            className={className}
            feedback={
              <FormFeedback
                error={error?.message}
                errorId={`${controlId}-error`}
                errorCyTag={errorCyTag ?? (cyTag ? `${cyTag}-error` : undefined)}
                description={description}
                helperId={`${controlId}-helper`}
                reserveSpace={reserveFeedbackSpace}
              />
            }
          >
            <div className={classNames(rowReverse ? 'flex flex-row-reverse gap-4' : 'flex')}>
              {label && (
                <div className="flex mr-3 flex-col flex-1">
                  <FormLabel
                    id={`${controlId}-label`}
                    htmlFor={controlId}
                    label={label}
                    tooltip={tooltip}
                    required={required}
                  />
                </div>
              )}
              <span className="relative size-6 shrink-0">
                <input
                  id={controlId}
                  name={field.name}
                  ref={field.ref}
                  type="checkbox"
                  disabled={disabled}
                  data-cy={cyTag || ''}
                  checked={!!field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  aria-invalid={!!error || undefined}
                  aria-required={required || undefined}
                  aria-describedby={
                    error?.message
                      ? `${controlId}-error`
                      : description
                        ? `${controlId}-helper`
                        : undefined
                  }
                  aria-errormessage={error?.message ? `${controlId}-error` : undefined}
                  className={classNames(
                    'peer size-6 shrink-0 appearance-none cursor-pointer rounded-md bg-grey-900 p-1',
                    'border border-grey-800 transition-colors duration-150',
                    'checked:bg-primary-50 checked:border-primary-50',
                    'focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-primary-50',
                    'disabled:cursor-not-allowed disabled:opacity-60',
                    props.style && props.style,
                    error && 'border-error-50 ring-3 ring-error-50/8'
                  )}
                />
                <CheckIcon
                  aria-hidden="true"
                  className="pointer-events-none absolute left-1 top-1 hidden size-4 fill-white peer-checked:block"
                />
              </span>
            </div>
          </FormField>
        );
      }}
    />
  );
};
